/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { useRef } from 'preact/hooks';
import { SelectionDetail } from '../UNSAFE_Collection';
import { Flex } from '../UNSAFE_Flex';
import { usePress } from '../hooks/UNSAFE_usePress';
import { styles } from '../UNSAFE_Selector/themes/SelectorStyles.css';
import { StyledCheckbox } from '../PRIVATE_StyledCheckbox';
import { mergeProps } from '../utils/UNSAFE_mergeProps';
import { useActive } from '../hooks/UNSAFE_useActive';
import { useFocus } from '../hooks/UNSAFE_useFocus';
import { useTestId, TestIdProps } from '../hooks/UNSAFE_useTestId';
import { useTooltip } from '../hooks/UNSAFE_useTooltip';
import { useTranslationBundle } from '../hooks/UNSAFE_useTranslationBundle';
import type { BundleType } from '../resources/nls/bundle';
import { useHover } from '../hooks/UNSAFE_useHover/useHover';

/**
 * Props for the SelectorAll Component
 */
export type Props<K> = {
  /**
   * The selected state of the select all selector
   */
  selected: 'none' | 'all' | 'partial' | 'partial-all';
  /**
   * Callback function to invoke when the selected keys has changed when
   * the checkbox is toggled.
   */
  onChange?: (detail: SelectionDetail<K>) => void;
  /**
   * aria-label for this selectorAll
   */
  'aria-label'?: string;
  /**
   * One or more ids (separated by spaces) of elements that label the selected item. This will be used for the aria-labelledby of the DOM element.
   */
  'aria-labelledby'?: string;
  /**
   * Specifies whether tooltip should be shown on the SelectorAll checkbox
   */
  showTooltip?: 'enabled' | 'disabled';
};

/**
 * The SelectorAll component renders checkboxes for select all case.
 */
export function SelectorAll<K>({
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledby,
  selected,
  onChange,
  showTooltip = 'disabled',
  testId
}: Props<K> & TestIdProps) {
  const pressHandler = (event: Event) => {
    if (selected == 'none' || selected == 'partial') {
      onChange?.({ value: { all: true, deletedKeys: new Set() }, target: event.target });
    } else {
      onChange?.({ value: { all: false, keys: new Set() }, target: event.target });
    }
  };

  const inputRef = useRef<HTMLInputElement>(null);
  const { isActive, activeProps } = useActive();
  const { isFocus, focusProps } = useFocus();
  const { pressProps } = usePress(pressHandler);

  const { isHover, hoverProps } = useHover();
  const isChecked =
    selected === 'partial' || selected === 'partial-all'
      ? 'partial'
      : selected === 'all'
      ? 'checked'
      : 'unchecked';
  const testIdProps = useTestId(testId);

  const onClick = () => {
    // ensure the checkbox is focused after click
    inputRef.current?.focus();
  };

  const translations = useTranslationBundle<BundleType>('@oracle/oraclejet-preact');

  const tooltipLabel =
    selected === 'none' || selected === 'partial'
      ? translations.selectorAll_selectAll()
      : translations.selectorAll_deselectAll();

  const { tooltipContent, tooltipProps } = useTooltip({
    text: tooltipLabel,
    isDisabled: showTooltip === 'disabled' ? true : false
  });

  const ariaLabelText = !ariaLabel && !ariaLabelledby ? tooltipLabel : undefined;

  return (
    <div
      class={styles.container}
      {...mergeProps({ onClick }, pressProps, tooltipProps)}
      {...testIdProps}>
      <div class={styles.base} {...activeProps} {...hoverProps}>
        <Flex align="center" justify="center">
          <StyledCheckbox
            ref={inputRef}
            isHover={isHover}
            isChecked={isChecked}
            isActive={isActive}
            isFocusRingShown={isFocus}
            aria-label={ariaLabel ? ariaLabel : ariaLabelText}
            aria-labelledby={ariaLabelledby}
            onKeyDown={handleKeyboardEvent}
            onKeyUp={handleKeyboardEvent}
            {...focusProps}
          />
          {tooltipContent}
        </Flex>
      </div>
    </div>
  );
}

const handleKeyboardEvent = (event: KeyboardEvent) => {
  if (event.key == 'Enter') {
    // prevent active styles applied to checkbox
    event.stopPropagation();
  }
};
